import { NextSeo } from "next-seo";
import { Navbar } from "~/components/Navbar/component";
import { PageHeader } from "~/components/PageHeader";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { ASSET_CLASS_IDS, MEASURE_SLUGS } from "~/consts";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { getCommoditiesTimeSeriesModes } from "~/modules/asset_classes/components/CommoditiesTimeSeriesCard";
import { ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS } from "~/modules/asset_classes/consts";
import { AssetTimeseriesColumns } from "~/modules/asset_timeseries_v4/types";
import { FilterOperators } from "~/modules/filters/types";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { DistributionSelectProps } from "~/modules/visualization/components/MetricTimeseriesChart/types";
import {
    getTimeseriesModes,
    MetricTypes,
} from "~/modules/visualization/components/Timeseries/getTimeseriesModes";
import {
    VisualizationModeCard,
    Props as VisualizationModeCardProps,
} from "~/modules/visualization/components/Timeseries/VisualizationModeCard";
import { THREE_YEARS_AGO } from "~/utils/dates/consts";
import { VisualizationGrid } from "~/modules/visualization/components/VisualizationGrid";
import { ColumnSpanConfig } from "~/types";
import { Routes } from "~/routes";

// // // //
// NOTE - prototype page: intentionally not registered in routes.ts or the
// sidebar nav. Each chart below mirrors the timeseriesChart configuration of
// the corresponding asset class individual page (src/pages/<asset-class>/).

const FULL_WIDTH_COLUMN_SPAN: ColumnSpanConfig = {
    sm: 12,
    md: 12,
    lg: 12,
    xl: 12,
    xxl: 6,
};

const STANDARD_DISTRIBUTION_SELECT: DistributionSelectProps = {
    distributed: {
        enabled: true,
    },
    represented: {
        enabled: true,
    },
    stablecoins: {
        enabled: false,
        active: false,
    },
};

function getAssetClassCharts(): Array<{
    key: string;
    chart: VisualizationModeCardProps;
}> {
    return [
        {
            key: "stablecoins",
            chart: {
                title: "Stablecoin Metrics",
                modes: getTimeseriesModes({
                    analyticsKey: "stablecoin",
                    defaultGroupBy: TokenTimeseriesGroupBy.asset,
                    measureSlug: MEASURE_SLUGS.bridged_token_market_cap_dollar,
                    groupByOptions: [
                        TokenTimeseriesGroupBy.asset,
                        TokenTimeseriesGroupBy.token,
                        TokenTimeseriesGroupBy.network,
                        TokenTimeseriesGroupBy.protocol,
                        TokenTimeseriesGroupBy.manager,
                        TokenTimeseriesGroupBy.jurisdictionCountry,
                    ],
                    navigator: {
                        enabled: true,
                        active: true,
                        forceUpdate: true,
                        xAxis: {
                            min: THREE_YEARS_AGO.getTime(),
                            max: null,
                        },
                    },
                    filters:
                        ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.stablecoins,
                }),
                distributionSelect: {
                    distributed: {
                        enabled: false,
                    },
                    represented: {
                        enabled: false,
                    },
                    stablecoins: {
                        enabled: false,
                        active: true,
                    },
                },
            },
        },
        {
            key: "treasuries",
            chart: {
                title: "Treasury Fund Metrics",
                modes: getTimeseriesModes({
                    analyticsKey: "treasury",
                    defaultGroupBy: TokenTimeseriesGroupBy.asset,
                    filters:
                        ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.treasuries,
                    assetTimeseriesFilters: [
                        {
                            field: AssetTimeseriesColumns.assetClassID,
                            operator: FilterOperators.Equals,
                            value: ASSET_CLASS_IDS.TreasuryProduct,
                        },
                        {
                            field: AssetTimeseriesColumns.isInvestable,
                            operator: FilterOperators.Equals,
                            value: true,
                        },
                    ],
                    metrics: [
                        MetricTypes.TotalValue,
                        MetricTypes.AssetHolders,
                        MetricTypes.ActiveAddresses,
                        MetricTypes.TransferVolume,
                        MetricTypes.MintBurnVolume,
                        MetricTypes.Apy7Day,
                    ],
                }),
                distributionSelect: STANDARD_DISTRIBUTION_SELECT,
            },
        },
        {
            key: "government-bonds",
            chart: {
                title: "Tokenized Non-U.S. Government Debt Metrics",
                modes: getTimeseriesModes({
                    analyticsKey: "government-bonds",
                    defaultGroupBy: TokenTimeseriesGroupBy.asset,
                    filters:
                        ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.government_bonds,
                }),
                distributionSelect: STANDARD_DISTRIBUTION_SELECT,
            },
        },
        {
            key: "credit",
            chart: {
                title: "Tokenized Credit Metrics",
                modes: getTimeseriesModes({
                    analyticsKey: "credit",
                    defaultGroupBy: TokenTimeseriesGroupBy.asset,
                    measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
                    filters:
                        ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.credit,
                }),
                distributionSelect: STANDARD_DISTRIBUTION_SELECT,
            },
        },
        {
            key: "stocks",
            chart: {
                title: "Tokenized Stock Metrics",
                modes: getTimeseriesModes({
                    analyticsKey: "stocks",
                    defaultGroupBy: TokenTimeseriesGroupBy.asset,
                    measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
                    filters:
                        ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.stocks,
                    groupByOptions: [
                        TokenTimeseriesGroupBy.asset,
                        TokenTimeseriesGroupBy.token,
                        TokenTimeseriesGroupBy.peggedAssetType,
                        TokenTimeseriesGroupBy.network,
                        TokenTimeseriesGroupBy.protocol,
                    ],
                    groupByOptionsWithNewIndicator: [
                        TokenTimeseriesGroupBy.peggedAssetType,
                    ],
                    groupByOptionsLabelOverrides: {
                        [TokenTimeseriesGroupBy.peggedAssetType]:
                            "Underlying Type",
                    },
                }),
                // NOTE - no distributionSelect, matching the stocks page. The
                // stocks page's "Underlying Type" segment select + series
                // merge are page-local and omitted from this prototype.
            },
        },
        {
            key: "private-equity-venture-capital",
            chart: {
                title: "Tokenized PE / VC Metrics",
                modes: getTimeseriesModes({
                    analyticsKey: "private-equity",
                    defaultGroupBy: TokenTimeseriesGroupBy.asset,
                    measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
                    filters: ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.pe_vc,
                    metrics: [
                        MetricTypes.TotalValue,
                        MetricTypes.AssetHolders,
                        MetricTypes.ActiveAddresses,
                        MetricTypes.TransferVolume,
                        MetricTypes.MintBurnVolume,
                    ],
                }),
                distributionSelect: STANDARD_DISTRIBUTION_SELECT,
            },
        },
        {
            key: "active-strategies",
            chart: {
                title: "Tokenized Active Strategies Metrics",
                modes: getTimeseriesModes({
                    analyticsKey: "actively-managed-strategies",
                    defaultGroupBy: TokenTimeseriesGroupBy.asset,
                    filters:
                        ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.active_strategies,
                }),
                distributionSelect: STANDARD_DISTRIBUTION_SELECT,
            },
        },
        {
            key: "commodities",
            chart: {
                title: "Tokenized Commodity Metrics",
                modes: getCommoditiesTimeSeriesModes({
                    filters: [
                        {
                            operator: FilterOperators.And,
                            filters: [
                                {
                                    field: TokenTimeseriesColumns.assetClassID,
                                    operator: FilterOperators.Equals,
                                    value: ASSET_CLASS_IDS.Commodities,
                                },
                            ],
                        },
                    ],
                    defaultGroupBy: TokenTimeseriesGroupBy.asset,
                    enableGroupByControls: true,
                }),
                distributionSelect: STANDARD_DISTRIBUTION_SELECT,
            },
        },
        {
            key: "real-estate",
            chart: {
                title: "Tokenized Real Estate Metrics",
                modes: getTimeseriesModes({
                    analyticsKey: "real-estate",
                    defaultGroupBy: TokenTimeseriesGroupBy.asset,
                    filters:
                        ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.real_estate,
                }),
                distributionSelect: STANDARD_DISTRIBUTION_SELECT,
            },
        },
    ];
}

// // // //

export default function AssetClassesPage() {
    const charts = getAssetClassCharts();

    return (
        <PageTrack route={Routes.assetClasses}>
            <NextSeo title="Asset Classes" noindex nofollow />
            <UserFetcher>
                {({ user }) => (
                    <Navbar route={Routes.assetClasses} user={user} />
                )}
            </UserFetcher>
            <SidebarLayout pathname="/asset-classes">
                <PageWrapper>
                    <PageHeader
                        title="Asset Classes"
                        subtitle="Timeseries metrics for each tokenized asset class"
                    />
                    <VisualizationGrid>
                        {charts.map(({ key, chart }) => (
                            <VisualizationModeCard
                                key={key}
                                {...chart}
                                columnSpanConfig={FULL_WIDTH_COLUMN_SPAN}
                            />
                        ))}
                    </VisualizationGrid>
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
